// Unpublished Work © 2021-2024 Deere & Company.
import {createUiAxiosClient, createUiAxiosClientWithSignal} from 'Utils/ui-axios-client';

async function getWorkboard(workboardId, signal = null) {
    const axiosClient = createUiAxiosClientWithSignal(signal);

    const response = await axiosClient.get(`/api/workboards/${workboardId}`);

    return response.data;
}

async function getWorkboardSelectedJobs(workboardId, signal = null) {
    const axiosClient = createUiAxiosClientWithSignal(signal);

    const response = await axiosClient.get(`/api/workboardJobs/workboard/${workboardId}`);

    if (response?.data?.success && response.data?.workboard_jobs) {
        return response.data.workboard_jobs;
    }

    return response.data;
}

async function getWorkboardSelectedOperators(workboardId, signal = null) {
    const axiosClient = createUiAxiosClientWithSignal(signal);

    const response = await axiosClient.get(`/api/workboardOperators/workboard/${workboardId}`);

    if (response?.data?.success && response.data?.workboard_operators) {
        return response.data.workboard_operators;
    }

    return response.data;
}

async function getWorkboardV2(workboardId, signal = null) {
    const axiosClient = createUiAxiosClientWithSignal(signal);

    const response = await axiosClient.get(`/api/workboards/${workboardId}`);

    if (response.data.success && response.data.workboard) {
        const selectedJobs = await getWorkboardSelectedJobs(workboardId, signal);
        const selectedOperators = await getWorkboardSelectedOperators(workboardId, signal);

        return {
            ...response.data.workboard,
            selectedJobs,
            selectedOperators,
            userOrder: response.data.userOrder ?? [],
            success: response.data.success
        };
    }

    return response.data;
}

async function getWorkboardPresentation(workboardId) {
    const axiosClient = createUiAxiosClient();

    const response = await axiosClient.get(`/api/workboards/presentation/${workboardId}`);

    return response.data;
}

async function getUserOrder(workboardId) {
    const axiosClient = createUiAxiosClient();

    const response = await axiosClient.get(`/api/workboards/${workboardId}/userOrder`);

    return response.data;
}

async function postWorkboard(workboardData) {
    const axiosClient = createUiAxiosClient();

    const response = await axiosClient.post('/api/workboards', workboardData);

    return response.data;
}

async function putWorkboard(workboardId, workboardData) {
    const axiosClient = createUiAxiosClient();

    const response = await axiosClient.put(`/api/workboards/${workboardId}`, workboardData);

    return response.data;
}

async function deleteWorkboard(workboardId) {
    const axiosClient = createUiAxiosClient();

    const response = await axiosClient.delete(`/api/workboards/${workboardId}`);

    return response.data;
}

async function getWorkboardTemplate(workboardId, signal = null) {
    const axiosClient = createUiAxiosClientWithSignal(signal);

    const response = await axiosClient.get(`/api/workboardTemplateWorkboards/${workboardId}`);

    return response.data;
}

async function getWorkboardTemplateWithDetails(workboardId, signal) {
    const axiosClient = createUiAxiosClientWithSignal(signal);

    const response = await axiosClient.get(`/api/workboardTemplateWorkboards/${workboardId}/details`);

    if (response?.data?.success && response.data?.workboardTemplateWorkboard) {
        return response.data.workboardTemplateWorkboard;
    }

    return response.data;
}

async function postWorkboardTemplate(workboardData) {
    const axiosClient = createUiAxiosClient();

    const response = await axiosClient.post('/api/workboardTemplateWorkboards', workboardData);

    return response.data;
}

async function postWorkboardTemplateWorkItems(membershipId, {
    workItems, workboardTemplateWorkboardId, workItemIdsToDelete = []
}) {
    const axiosClient = createUiAxiosClient();

    const response = await axiosClient.post('/api/workboardTemplateWorkboards/workItems/batch', {
        membershipId,
        workItems,
        workboardTemplateWorkboardId,
        workItemIdsToDelete
    });

    return response.data;
}

async function putWorkboardTemplate(workboardId, workboardData) {
    const axiosClient = createUiAxiosClient();

    const response = await axiosClient.put(`/api/workboardTemplateWorkboards/${workboardId}`, workboardData);

    return response.data;
}

async function deleteWorkboardTemplate(workboardId) {
    const axiosClient = createUiAxiosClient();

    const response = await axiosClient.delete(`/api/workboardTemplateWorkboards/${workboardId}`);

    return response.data;
}

export {
    getWorkboard,
    getWorkboardV2,
    getWorkboardPresentation,
    getUserOrder,
    postWorkboard,
    putWorkboard,
    deleteWorkboard,
    getWorkboardTemplate,
    postWorkboardTemplate,
    putWorkboardTemplate,
    deleteWorkboardTemplate,
    getWorkboardTemplateWithDetails,
    postWorkboardTemplateWorkItems,
    getWorkboardSelectedJobs,
    getWorkboardSelectedOperators
};
